import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Printer, Copy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { PaymentMethodIcon, methodLabel } from "@/components/PaymentMethodIcon";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fmt = (n, ccy) => new Intl.NumberFormat(undefined, { style: "currency", currency: ccy || "SGD" }).format(n || 0);

export default function PublicReceipt() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND}/api/public/receipts/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setError("Receipt not found or link expired."));
  }, [slug]);

  if (error) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">{error}</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading receipt…</div>;

  const { payment, invoice, running, billing_profile } = data;
  const ccy = invoice.currency || "SGD";
  const fullyPaid = invoice.balance_due <= 0.005;
  const accent = billing_profile?.accent_color || "#7C3AED";
  const logoUri = billing_profile?.logo_data_uri || null;

  return (
    <div className="min-h-screen bg-white text-black" data-testid="public-receipt">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { margin: 15mm 12mm; size: A4; }
          body { background: white !important; }
        }
        .receipt-page { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; color: #1a1a1a; }
        .receipt-page h1 { letter-spacing: -0.02em; }
      `}</style>

      {/* Branded accent ribbon */}
      <div className="h-1.5 w-full" style={{ background: accent }} data-testid="receipt-accent-ribbon" />

      {/* Print action bar */}
      <div className="no-print sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between shadow-sm">
        <div className="text-sm">
          <span className="font-bold">Receipt</span> · Invoice {invoice.invoice_number}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="rounded-sm" onClick={() => { navigator.clipboard.writeText(window.location.href).then(() => toast.success("URL copied")).catch(() => toast.error("Copy failed")); }} data-testid="public-receipt-copy-url">
            <Copy className="h-4 w-4 mr-2" />Copy link
          </Button>
          <Button className="rounded-sm text-white" style={{ backgroundColor: accent }} onClick={() => window.print()} data-testid="public-receipt-print">
            <Printer className="h-4 w-4 mr-2" />Print
          </Button>
        </div>
      </div>

      <div className="receipt-page max-w-3xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="flex items-start justify-between pb-6 border-b-2" style={{ borderColor: accent }}>
          <div className="flex items-start gap-4">
            {logoUri && (
              <img
                src={logoUri}
                alt="Company logo"
                className="h-16 w-16 object-contain rounded-sm bg-white border border-slate-200 p-1.5 shrink-0"
                data-testid="receipt-logo"
              />
            )}
            <div>
              {(billing_profile?.company_name || billing_profile?.division_name) && (
                <div className="text-2xl font-black tracking-tight" data-testid="receipt-company-name">{billing_profile?.company_name || billing_profile?.division_name}</div>
              )}
              {billing_profile?.company_name && billing_profile?.division_name && billing_profile.company_name !== billing_profile.division_name && (
                <div className="text-xs text-slate-600 font-semibold mt-0.5" data-testid="receipt-division-name">{billing_profile.division_name}</div>
              )}
              {billing_profile?.uen && <div className="text-xs text-slate-500 uppercase tracking-widest mt-1">UEN {billing_profile.uen}</div>}
              {billing_profile?.address && <div className="text-xs text-slate-500 mt-1">{billing_profile.address}{billing_profile?.postal_code ? ` ${billing_profile.postal_code}` : ""}</div>}
              <div className="text-xs text-slate-500 mt-1">
                {[billing_profile?.email, billing_profile?.phone, billing_profile?.website].filter(Boolean).join(" · ")}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color: accent }}>Payment Receipt</div>
            <h1 className="text-3xl font-black mt-1">{invoice.invoice_number}</h1>
            <div className="text-xs text-slate-600 mt-1">Payment date: <span className="font-semibold">{payment.date}</span></div>
          </div>
        </div>

        {/* Amount received hero */}
        <div
          className="mt-8 rounded-sm p-6 flex items-center justify-between"
          style={{ background: `${accent}0D`, border: `1.5px solid ${accent}` }}
          data-testid="receipt-hero"
        >
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-bold">Amount received</div>
            <div className="text-4xl font-black mt-2 tracking-tight">{fmt(payment.amount, ccy)}</div>
            <div className="mt-3 inline-flex items-center gap-2 rounded-sm bg-white border border-slate-200 px-2.5 py-1.5 text-xs" data-testid="receipt-method-chip">
              <PaymentMethodIcon method={payment.method} className="h-3.5 w-3.5" />
              <span className="font-semibold text-slate-800">{methodLabel(payment.method)}</span>
              {payment.reference && <span className="text-slate-500">· Ref: <span className="font-semibold text-slate-800">{payment.reference}</span></span>}
            </div>
          </div>
          <CheckCircle2 className="h-16 w-16 text-emerald-500" strokeWidth={1.5} />
        </div>

        {/* Balance summary */}
        <div className="grid grid-cols-3 gap-4 mt-6">
          <SummaryTile label="Invoice total" value={fmt(invoice.total, ccy)} tone="neutral" />
          <SummaryTile label="Paid to date" value={fmt(invoice.paid_total, ccy)} tone="green" />
          <SummaryTile
            label={fullyPaid ? "Balance" : "Outstanding"}
            value={fmt(invoice.balance_due, ccy)}
            tone={fullyPaid ? "green" : "warn"}
            testId="receipt-balance"
          />
        </div>

        {/* Payments to date */}
        {running.payments_before?.length > 0 && (
          <div className="mt-8">
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-bold mb-2">Prior payments on this invoice</div>
            <div className="border border-slate-200 rounded-sm">
              <div className="grid grid-cols-[1fr_120px_120px] gap-2 px-3 py-2 border-b border-slate-200 bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                <div>Date</div><div>Method</div><div className="text-right">Amount</div>
              </div>
              {running.payments_before.map((p, i) => (
                <div key={i} className="grid grid-cols-[1fr_120px_120px] gap-2 px-3 py-2 border-b border-slate-100 last:border-b-0 text-sm items-center">
                  <div>{p.date}</div>
                  <div className="text-slate-600 inline-flex items-center gap-1.5"><PaymentMethodIcon method={p.method} className="h-3 w-3" /><span>{methodLabel(p.method)}</span></div>
                  <div className="text-right font-semibold">{fmt(p.amount, ccy)}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {payment.note && (
          <div className="mt-8">
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-bold mb-1">Note</div>
            <div className="text-sm">{payment.note}</div>
          </div>
        )}

        {/* Bill-to */}
        {(invoice.bill_to?.name || invoice.bill_to?.email) && (
          <div className="mt-10 pt-6 border-t border-slate-200 text-xs text-slate-500">
            Issued to <span className="text-slate-800 font-semibold">{invoice.bill_to?.name || invoice.bill_to?.email}</span>
            {invoice.bill_to?.email && invoice.bill_to?.name && <> · {invoice.bill_to.email}</>}
          </div>
        )}

        {/* Footer note */}
        <div className="mt-8 text-[11px] text-slate-500 text-center">
          Thank you for your payment. Retain this receipt for your records.
        </div>
      </div>
    </div>
  );
}

function SummaryTile({ label, value, tone = "neutral", testId }) {
  const cls = {
    neutral: "text-slate-900 bg-slate-50",
    green: "text-emerald-600 bg-emerald-50",
    warn: "text-amber-600 bg-amber-50",
  }[tone];
  return (
    <div className={`rounded-sm border border-slate-200 p-4 ${cls}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.25em] font-bold opacity-70">{label}</div>
      <div className="text-2xl font-black mt-1 tracking-tight">{value}</div>
    </div>
  );
}
